import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import moment from 'moment';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { Group } from '../models/group.model';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  http = inject(HttpClient);
  utilsService = inject(UtilsService);

  constructor() {}

  async getGroupById(groupId: string): Promise<Group | null | undefined> {
    try {
      const baseParams = new HttpParams().set('groupId', groupId);
      const headers = await this.utilsService.setHttpHeaders(
        false,
        undefined,
        environment['AWS_SEARCH_API_KEY']
      );

      const response$ = this.http.get<Group | null | undefined>(
        environment['AWS_SEARCH_GROUPS_BY_ID']!,
        {
          params: baseParams,
          headers: headers,
        }
      );

      return lastValueFrom(response$.pipe(take(1)));
    } catch (error) {
      console.warn(`AWS search API getGroupById notice:`, error);
      return null;
    }
  }

  async getGroupsByLocation(
    lat: number,
    lon: number,
    limitDistance: string = '25km',
    limitRecords: number = 10
  ): Promise<Group[]> {
    try {
      const baseParams = new HttpParams()
        .set('lat', lat.toString())
        .set('lon', lon.toString())
        .set('limitDistance', limitDistance)
        .set('limitRecords', limitRecords.toString());

      const headers = await this.utilsService.setHttpHeaders(
        false,
        undefined,
        environment['AWS_SEARCH_API_KEY']
      );

      const response$ = this.http.get<{ result: Group[] }>(
        environment['AWS_SEARCH_GROUPS_BY_LOCATION']!,
        {
          params: baseParams,
          headers: headers,
        }
      );

      const response = await lastValueFrom(response$.pipe(take(1)));
      return response?.result || [];
    } catch (error) {
      console.warn(`AWS search API getGroupsByLocation notice:`, error);
      return [];
    }
  }

  async getEventsByLocation(
    lat: number,
    lon: number,
    limitDistance: string = '25km',
    limitRecords: number = 15
  ): Promise<any[]> {
    try {
      const startDateTime = new Date().toISOString();
      const baseParams = new HttpParams()
        .set('lat', lat.toString())
        .set('lon', lon.toString())
        .set('startDateTime', startDateTime)
        .set('limitDistance', limitDistance)
        .set('limitRecords', limitRecords.toString());

      const headers = await this.utilsService.setHttpHeaders(
        false,
        undefined,
        environment['AWS_API_KEY']
      );

      const response$ = this.http.get<{ result: any[] }>(
        environment['AWS_SEARCH_EVENTS_NEARME']!,
        {
          params: baseParams,
          headers: headers,
        }
      );

      const response = await lastValueFrom(response$.pipe(take(1)));
      const rawEvents = response?.result || [];
      const now = moment();
      const maxStartDate = moment().add(5, 'days');

      const filtered = rawEvents.filter((e) => {
        if (e.isPrivate === true) return false;

        const start = e.startDateTime?.toDate ? moment(e.startDateTime.toDate()) : (e.startDateTime ? moment(e.startDateTime) : null);
        const end = e.endDateTime?.toDate ? moment(e.endDateTime.toDate()) : (e.endDateTime ? moment(e.endDateTime) : null);

        const isStartValid = start ? start.isSameOrBefore(maxStartDate) : true;
        const isEndValid = end ? end.isSameOrAfter(now) : (start ? start.isSameOrAfter(now) : true);

        return isStartValid && isEndValid;
      });

      return filtered.slice(0, 15);
    } catch (error) {
      console.warn(`AWS search API getEventsByLocation notice:`, error);
      return [];
    }
  }
}
